import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '@/assets';
import { cx } from '@/utils/cx';

interface DateFieldProps {
    name: string;
    label: string;
    value?: string;
    onChange?: (val: string) => void;
    onBlur?: (val: string) => void;
    error?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    showTimePicker?: boolean;
}

const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];

const WEEK_DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (value: number) => String(value).padStart(2, '0');

const formatDate = (date: Date) =>
    `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

const parseDateValue = (value?: string) => {
    const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
    if (!match) return null;

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const hour = match[4] !== undefined ? Number(match[4]) : 0;
    const minute = match[5] !== undefined ? Number(match[5]) : 0;
    const date = new Date(year, month - 1, day, hour, minute);

    if (
        date.getFullYear() !== year ||
        date.getMonth() !== month - 1 ||
        date.getDate() !== day ||
        date.getHours() !== hour ||
        date.getMinutes() !== minute
    ) {
        return null;
    }

    return date;
};

const toTwelveHourTime = (date: Date) => {
    const hours = date.getHours();
    const hour12 = hours % 12 || 12;
    return {
        hour: pad(hour12),
        minute: pad(date.getMinutes()),
        period: hours >= 12 ? 'PM' : 'AM',
    };
};

const toTwentyFourHour = (hour: string, minute: string, period: string) => {
    let parsedHour = Number(hour);
    if (period === 'AM' && parsedHour === 12) parsedHour = 0;
    if (period === 'PM' && parsedHour !== 12) parsedHour += 12;
    return `${pad(parsedHour)}:${minute}`;
};

const formatDateTimeDisplay = (value?: string) => {
    const date = parseDateValue(value);
    if (!date) return value || '';

    const time = toTwelveHourTime(date);
    return `${formatDate(date)} ${time.hour}:${time.minute} ${time.period}`;
};

const buildMonthDays = (visibleDate: Date) => {
    const year = visibleDate.getFullYear();
    const month = visibleDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = firstDay.getDay();
    const cells: Array<number | null> = Array.from({ length: leadingBlanks }, () => null);

    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push(day);
    }

    while (cells.length % 7 !== 0) {
        cells.push(null);
    }

    return cells;
};

interface PickerSelectOption {
    label: string;
    value: string;
}

interface PickerSelectProps {
    value: string;
    options: PickerSelectOption[];
    onChange: (value: string) => void;
    className?: string;
    searchPlaceholder?: string;
}

const PickerSelect = ({
    value,
    options,
    onChange,
    className,
    searchPlaceholder = 'Search...',
}: PickerSelectProps) => {
    const selectRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const selectedOption = options.find((option) => option.value === value);
    const filteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    useEffect(() => {
        if (!isOpen) return;

        searchRef.current?.focus();
        const handleClickOutside = (event: MouseEvent) => {
            if (!selectRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    return (
        <div ref={selectRef} className={cx("relative min-w-0", className)}>
            <button
                type="button"
                onClick={() => {
                    setSearchTerm('');
                    setIsOpen((current) => !current);
                }}
                className="flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 text-left text-sm text-gray-90 outline-none hover:bg-gray-50 focus:border-brand"
            >
                <span className="min-w-0 truncate">{selectedOption?.label || 'Select'}</span>
                <Icons.ChevronDownIcon className={cx("h-4 w-4 shrink-0 text-gray-500 transition-transform", isOpen && "rotate-180")} />
            </button>

            {isOpen && (
                <div className="absolute left-0 top-full z-[520] w-full rounded-[10px] border border-gray-200 bg-white shadow-lg">
                    <div className="p-2">
                        <input
                            ref={searchRef}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="h-9 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-brand"
                        />
                    </div>
                    <div className="max-h-[148px] overflow-y-auto py-1 scrollbar-hide">
                        {filteredOptions.length > 0 ? filteredOptions.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                    onChange(option.value);
                                    setIsOpen(false);
                                    setSearchTerm('');
                                }}
                                className={cx(
                                    "block w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50",
                                    option.value === value && "bg-gray-50 font-medium"
                                )}
                            >
                                {option.label}
                            </button>
                        )) : (
                            <div className="px-4 py-3 text-sm text-gray-400">No options found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export const DateField = forwardRef<HTMLInputElement, DateFieldProps>(({
    name,
    label,
    value,
    onChange,
    onBlur,
    error,
    placeholder = 'DD/MM/YYYY',
    required,
    disabled,
    showTimePicker = false,
}, ref) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLDivElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const parsedValue = parseDateValue(value);
    const initialDate = parsedValue || new Date();
    const initialTime = toTwelveHourTime(initialDate);
    const [isOpen, setIsOpen] = useState(false);
    const [visibleDate, setVisibleDate] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), 1));
    const [selectedDate, setSelectedDate] = useState<Date | null>(parsedValue);
    const [hour, setHour] = useState(initialTime.hour);
    const [minute, setMinute] = useState(initialTime.minute);
    const [period, setPeriod] = useState(initialTime.period);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0, width: 0 });

    const displayValue = showTimePicker ? formatDateTimeDisplay(value) : value || '';
    const calendarDays = useMemo(() => buildMonthDays(visibleDate), [visibleDate]);
    const monthOptions = useMemo(
        () => MONTHS.map((month, index) => ({ label: month, value: String(index) })),
        []
    );
    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 151 }, (_, index) => currentYear - 75 + index);
    }, []);
    const yearOptions = useMemo(
        () => years.map((year) => ({ label: String(year), value: String(year) })),
        [years]
    );

    const updatePickerPosition = () => {
        if (!fieldRef.current) return;

        const rect = fieldRef.current.getBoundingClientRect();
        setPickerPosition({
            top: rect.bottom,
            left: rect.left,
            width: Math.max(rect.width, showTimePicker ? 340 : 312),
        });
    };

    const openPicker = () => {
        if (disabled) return;

        const nextDate = parseDateValue(value) || new Date();
        const nextTime = toTwelveHourTime(nextDate);
        setVisibleDate(new Date(nextDate.getFullYear(), nextDate.getMonth(), 1));
        setSelectedDate(parseDateValue(value));
        setHour(nextTime.hour);
        setMinute(nextTime.minute);
        setPeriod(nextTime.period);
        updatePickerPosition();
        setIsOpen(true);
    };

    const closePicker = (shouldBlur = true) => {
        if (!isOpen) return;

        setIsOpen(false);
        if (shouldBlur) onBlur?.(value || '');
    };

    const handleDateSelect = (day: number) => {
        const nextDate = new Date(visibleDate.getFullYear(), visibleDate.getMonth(), day);
        setSelectedDate(nextDate);

        if (!showTimePicker) {
            const formattedDate = formatDate(nextDate);
            onChange?.(formattedDate);
            setIsOpen(false);
            onBlur?.(formattedDate);
        }
    };

    const handleApplyDateTime = () => {
        const nextDate = selectedDate || new Date(visibleDate.getFullYear(), visibleDate.getMonth(), 1);
        const formattedValue = `${formatDate(nextDate)} ${toTwentyFourHour(hour, minute, period)}`;
        onChange?.(formattedValue);
        setIsOpen(false);
        onBlur?.(formattedValue);
    };

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            const target = e.target as Node;
            const clickedField = wrapperRef.current?.contains(target);
            const clickedPicker = pickerRef.current?.contains(target);

            if (!clickedField && !clickedPicker) {
                closePicker();
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, value, onBlur]);

    useEffect(() => {
        if (!isOpen) return;

        updatePickerPosition();
        const handleWindowChange = () => updatePickerPosition();
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closePicker(false);
        };

        window.addEventListener('resize', handleWindowChange);
        window.addEventListener('scroll', handleWindowChange, true);
        document.addEventListener('keydown', handleEscape);

        return () => {
            window.removeEventListener('resize', handleWindowChange);
            window.removeEventListener('scroll', handleWindowChange, true);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen]);

    const picker = isOpen && !disabled && typeof document !== 'undefined'
        ? createPortal(
            <div
                ref={pickerRef}
                style={{
                    top: pickerPosition.top,
                    left: pickerPosition.left,
                    width: pickerPosition.width,
                }}
                className="fixed z-[500] rounded-[10px] border border-gray-200 bg-white p-3 shadow-lg"
            >
                <div className="mb-3 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setVisibleDate(new Date(visibleDate.getFullYear(), visibleDate.getMonth() - 1, 1))}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
                    >
                        <Icons.ChevronLeftIcon className="h-4 w-4 text-gray-80" />
                    </button>
                    <PickerSelect
                        value={String(visibleDate.getMonth())}
                        options={monthOptions}
                        onChange={(nextMonth) => setVisibleDate(new Date(visibleDate.getFullYear(), Number(nextMonth), 1))}
                        className="flex-1"
                        searchPlaceholder="Search month..."
                    />
                    <PickerSelect
                        value={String(visibleDate.getFullYear())}
                        options={yearOptions}
                        onChange={(nextYear) => setVisibleDate(new Date(Number(nextYear), visibleDate.getMonth(), 1))}
                        className="w-[104px]"
                        searchPlaceholder="Search year..."
                    />
                    <button
                        type="button"
                        onClick={() => setVisibleDate(new Date(visibleDate.getFullYear(), visibleDate.getMonth() + 1, 1))}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50"
                    >
                        <Icons.ChevronRightIcon className="h-4 w-4 text-gray-80" />
                    </button>
                </div>

                <div className="grid grid-cols-7 gap-1">
                    {WEEK_DAYS.map((day) => (
                        <div key={day} className="py-1 text-center text-xs font-medium text-gray-50">{day}</div>
                    ))}
                    {calendarDays.map((day, index) => {
                        const isSelected = Boolean(
                            day &&
                            selectedDate &&
                            selectedDate.getDate() === day &&
                            selectedDate.getMonth() === visibleDate.getMonth() &&
                            selectedDate.getFullYear() === visibleDate.getFullYear()
                        );

                        return (
                            <button
                                key={`${day || 'blank'}-${index}`}
                                type="button"
                                disabled={!day}
                                onClick={() => day && handleDateSelect(day)}
                                className={cx(
                                    "h-9 rounded-lg text-sm transition-colors",
                                    !day && "cursor-default",
                                    day && !isSelected && "text-gray-90 hover:bg-gray-50",
                                    isSelected && "bg-brand text-white"
                                )}
                            >
                                {day || ''}
                            </button>
                        );
                    })}
                </div>

                {showTimePicker && (
                    <div className="mt-4 border-t border-gray-100 pt-3">
                        <div className="mb-2 text-sm font-medium text-gray-90">Time</div>
                        <div className="grid grid-cols-[1fr_1fr_1fr] gap-2">
                            <select
                                value={hour}
                                onChange={(e) => setHour(e.target.value)}
                                className="h-10 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-brand"
                            >
                                {Array.from({ length: 12 }, (_, index) => pad(index + 1)).map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                            <select
                                value={minute}
                                onChange={(e) => setMinute(e.target.value)}
                                className="h-10 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-brand"
                            >
                                {Array.from({ length: 60 }, (_, index) => pad(index)).map((item) => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </select>
                            <select
                                value={period}
                                onChange={(e) => setPeriod(e.target.value)}
                                className="h-10 rounded-lg border border-gray-200 px-2 text-sm outline-none focus:border-brand"
                            >
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                            </select>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => closePicker(false)}
                                className="h-9 rounded-lg border border-gray-200 px-4 text-sm font-medium text-gray-80 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleApplyDateTime}
                                className="h-9 rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand/90"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>,
            document.body
        )
        : null;

    return (
        <div ref={wrapperRef} className="flex w-full flex-col gap-2.5">
            <label htmlFor={name} className="text-base text-gray-90">
                {label} {required && <span className="ml-1 text-red-500">*</span>}
            </label>
            <div
                ref={fieldRef}
                onClick={openPicker}
                className={cx(
                    "relative w-full cursor-pointer rounded-[10px] p-[1.5px] transition-all duration-200",
                    error ? "bg-[#ff424f]" : "bg-gray-30 focus-within:bg-gradient-to-r focus-within:from-[#00B4EA] focus-within:to-[#8A00FF]",
                    disabled && "cursor-not-allowed bg-gray-200 opacity-60"
                )}
            >
                <div className="relative flex items-center overflow-hidden rounded-[8.5px] bg-white">
                    <input
                        ref={ref}
                        id={name}
                        name={name}
                        type="text"
                        placeholder={showTimePicker ? "DD/MM/YYYY hh:mm AM/PM" : placeholder}
                        value={displayValue}
                        disabled={disabled}
                        readOnly={showTimePicker}
                        onFocus={openPicker}
                        onChange={(e) => {
                            if (showTimePicker) return;
                            onChange?.(e.target.value);
                        }}
                        onBlur={(e) => {
                            if (!showTimePicker) onBlur?.(e.target.value);
                        }}
                        className={cx(
                            "h-[51px] w-full bg-transparent px-4 pr-10 text-sm text-gray-900 outline-none placeholder:text-gray-400",
                            showTimePicker ? "cursor-pointer" : "cursor-text",
                            disabled && "cursor-not-allowed bg-gray-50"
                        )}
                    />
                    <div className="absolute right-4 flex items-center cursor-pointer">
                        <Icons.CalendarIcon className="h-5 w-5 text-gray-500" />
                    </div>
                </div>
            </div>
            {picker}
            {error && <p className="text-xs font-medium text-error-60">{error}</p>}
        </div>
    );
});

DateField.displayName = 'DateField';
